import {isEqual} from 'lodash';
import {InMemoryCache, defaultDataIdFromObject} from 'apollo-cache-inmemory';
import {getQueryName} from '../../server/utils/graphqlUtils';
import {SEARCH_QUERY_NAME} from '../queries/searchQuery';
import {
    FILTER_COUNT_QUERY_NAME,
    FILTER_COUNT_SEARCH_QUERY_NAME
} from '../queries/filteredCountQuery';
import {SEARCH_REQUEST_QUERY_NAME} from '../queries/searchRequestQuery';

const getFilename = (src) => (src ? src.split('/').pop() : '');

const digest = (object) =>
    `${object.__typename}:${Object.values(object)
        .filter((v) => v !== object.__typename)
        .join('|')}`;

export const dataIdFromObject = (object) => {
    const typename = object.__typename;
    switch (typename) {
        /*
        When adding new IDs you must ensure that the format is such that
        it will be globally unique _across the entire schema_ used by SERP
        In addition the object you are making an ID for must not vary based
        on the particular search (e.g. filter counts).
        */
        case 'Badge':
            return `${typename}:${object.name}`;
        case 'HitImage':
        case 'Image':
            return `${typename}:${getFilename(
                object.src || object.lowResSrc || object.c6_uri
            )}`;
        case 'GeoCode':
        case 'Coordinates':
        case 'HitSuperlative':
        case 'Details':
            return digest(object);
        default:
            return defaultDataIdFromObject(object);
    }
};

/**
 * Optimized version of InMemoryCache which caches the first SearchQuery query for the initial
 * pageload to avoid the expensive normalization process for the server side page load for the
 * very large SearchQuery GraphQL query.
 */
class OptimizedInMemoryCache extends InMemoryCache {
    extract(optimistic) {
        const normalizedCache = super.extract(optimistic);
        return {_INITIAL_QUERY: this._INITIAL_QUERY, ...normalizedCache};
    }

    restore(data) {
        this._INITIAL_QUERY = data._INITIAL_QUERY;
        return super.restore(data);
    }

    reset() {
        this._INITIAL_QUERY = null;
        return super.reset();
    }

    write(write) {
        if (
            [SEARCH_QUERY_NAME, SEARCH_REQUEST_QUERY_NAME].includes(
                getQueryName(write.query)
            ) &&
            !this._INITIAL_QUERY
        ) {
            // Save the first SearchQuery query, don't normalize this to the cache
            this._INITIAL_QUERY = {
                result: write.result,
                variables: write.variables
            };
            super.broadcastWatches();
            return;
        }
        super.write(write);
    }

    read(query) {
        if (this.useInitialQuery(query)) {
            return this._INITIAL_QUERY.result;
        }
        return super.read(query);
    }

    diff(query) {
        if (this.useInitialQuery(query)) {
            return {result: this._INITIAL_QUERY.result, complete: true};
        }
        return super.diff(query);
    }

    useInitialQuery(query) {
        return (
            [
                FILTER_COUNT_SEARCH_QUERY_NAME,
                FILTER_COUNT_QUERY_NAME,
                SEARCH_QUERY_NAME,
                SEARCH_REQUEST_QUERY_NAME
            ].includes(getQueryName(query.query)) &&
            this._INITIAL_QUERY &&
            isEqual(this._INITIAL_QUERY.variables, query.variables)
        );
    }
}

export const createCache = () =>
    new OptimizedInMemoryCache({
        dataIdFromObject
    });
